/**
 * public/js/chapter-splitter/project.js — what a person builds in /chapter-splitter: the split points,
 * each chapter's name and fades, what is inserted before a chapter, the order of the files — with
 * one undo history and a plain-JSON form for the work store.
 *
 * WHY. The editor kept this in five loose variables and re-matched names, fades and inserts to the
 * chapters after every change BY START TIME (tolerance 10 ms): dragging a split point therefore
 * dropped the name, the fades and the insert of the chapter it starts. Undo snapshotted markers,
 * names and fades but not inserts or the order; the export took «the insert before file i» by the
 * position in the REORDERED list, so a reordered book got the jingles in front of the wrong files.
 * And nothing of it could be saved: the bug report of 21.10.2026 («все что выделили исчезло») is a
 * person who lost the same book's markers 23 times in 6 days.
 *
 * MODEL. A split point has an id that lives as long as the point (a drag moves it, it does not
 * replace it). Chapter 0 has the fixed key 0; chapter k > 0 is keyed by the id of the point it
 * starts at. Names and fades belong to the chapter key, an insert to the point it sits at (it is
 * played before the chapter that point starts). The order is a list of chapter keys.
 *
 *   var p = CsProject.create({ duration, minGap: 0.5 });
 *   p.chapters() → [{ index, key, start, end, duration, name, fadeIn, fadeOut, insert }]   (time order)
 *   p.ordered()  → the same, in the person's order (export)
 *   p.marks()    → [t, …] sorted
 *   p.addMark(t) / p.removeMark(sortedIdx) / p.drag(sortedIdx) → {to(t), end()} / p.replaceMarks(list, firstName)
 *   p.setName(i, s, commit) / p.setFade(i, 'fadeIn'|'fadeOut', v, commit) / p.setInsert(i, ins) / p.move(from, to)
 *   p.checkpoint() — the next live edit (typing, a slider) starts with an undo point
 *   p.undo() / p.redo() / p.canUndo() / p.canRedo()
 *   p.toJSON(files) — files: an array the File objects of audio inserts are pushed into
 *   CsProject.fromJSON(json, files, opts) → a project
 * Tests: tests/js/chapter-splitter-project.test.mjs
 */
(function (root) {
    'use strict';

    var UNDO_MAX = 100;

    function num(v, lo, hi, dflt) { return (typeof v === 'number' && isFinite(v)) ? Math.min(hi, Math.max(lo, v)) : dflt; }

    function create(o) {
        o = o || {};
        var duration = o.duration > 0 ? o.duration : 0;
        var minGap = o.minGap > 0 ? o.minGap : 0.5;
        var marks = [];          // [{id, t}] sorted by t
        var attrs = {};          // chapter key → {name, fadeIn, fadeOut}
        var ins = {};            // point id → {type:'silence', duration} | {type:'audio', file, name, duration}
        var order = null;        // [chapter key] or null = time order
        var nextId = 1;
        var undo = [], redo = [], armed = true, listeners = [];

        function changed(kind) { for (var i = 0; i < listeners.length; i++) { try { listeners[i](kind); } catch (e) { if (root.console) console.error(e); } } }
        function snap() {
            var a = {}, k;
            for (k in attrs) if (Object.prototype.hasOwnProperty.call(attrs, k)) a[k] = { name: attrs[k].name, fadeIn: attrs[k].fadeIn, fadeOut: attrs[k].fadeOut };
            var s = {};
            for (k in ins) if (Object.prototype.hasOwnProperty.call(ins, k)) s[k] = ins[k];   // inserts are immutable records
            return { marks: marks.map(function (m) { return { id: m.id, t: m.t }; }), attrs: a, ins: s, order: order ? order.slice() : null };
        }
        function restore(s) { marks = s.marks.map(function (m) { return { id: m.id, t: m.t }; }); attrs = s.attrs; ins = s.ins; order = s.order; }
        function record() {
            undo.push(snap());
            if (undo.length > UNDO_MAX) undo.shift();
            redo = [];
        }
        function keyOf(i) { return i === 0 ? 0 : (marks[i - 1] ? marks[i - 1].id : -1); }
        function attr(key) { return attrs[key] || null; }
        function fits(t, skipId) {
            if (t < minGap || t > duration - minGap) return false;
            for (var i = 0; i < marks.length; i++) if (marks[i].id !== skipId && Math.abs(marks[i].t - t) < minGap) return false;
            return true;
        }
        function sortMarks() { marks.sort(function (a, b) { return a.t - b.t; }); }
        /* drop what belongs to points and chapters that are gone */
        function prune() {
            var live = { 0: 1 }, k;
            for (var i = 0; i < marks.length; i++) live[marks[i].id] = 1;
            for (k in attrs) if (!live[k]) delete attrs[k];
            for (k in ins) if (!live[k]) delete ins[k];
            if (order) {
                var keep = order.filter(function (x) { return live[x]; });
                order = keep.length === marks.length + 1 ? keep : null;
            }
        }

        var api = {
            get duration() { return duration; },
            minGap: minGap,
            onChange: function (fn) { listeners.push(fn); },
            marks: function () { return marks.map(function (m) { return m.t; }); },
            chapters: function () {
                var pts = [0].concat(marks.map(function (m) { return m.t; })).concat([duration]), out = [];
                for (var i = 0; i < pts.length - 1; i++) {
                    var key = keyOf(i), a = attr(key);
                    out.push({
                        index: i, key: key, start: pts[i], end: pts[i + 1], duration: pts[i + 1] - pts[i],
                        name: a && a.name ? a.name : '', fadeIn: (a && a.fadeIn) || 0, fadeOut: (a && a.fadeOut) || 0,
                        insert: i > 0 ? (ins[key] || null) : null
                    });
                }
                return out;
            },
            ordered: function () {
                var ch = api.chapters();
                if (!order || order.length !== ch.length) return ch;
                var byKey = {};
                ch.forEach(function (c) { byKey[c.key] = c; });
                var out = order.map(function (k) { return byKey[k]; }).filter(Boolean);
                return out.length === ch.length ? out : ch;
            },
            reordered: function () { return !!order; },
            canAdd: function (t) { return fits(t, -1); },
            addMark: function (t) {
                if (!fits(t, -1)) return false;
                record();
                marks.push({ id: nextId++, t: t });
                sortMarks();
                order = null;
                changed('marks');
                return true;
            },
            removeMark: function (sortedIdx) {
                if (sortedIdx < 0 || sortedIdx >= marks.length) return false;
                record();
                marks.splice(sortedIdx, 1);
                order = null;
                prune();
                changed('marks');
                return true;
            },
            /* the chapter row's ×: the first chapter loses the point after it, the others the one before */
            removeChapter: function (i) { return marks.length ? api.removeMark(i === 0 ? 0 : i - 1) : false; },
            /* a drag is ONE undo step however many moves it makes; the point keeps its id */
            drag: function (sortedIdx) {
                var m = marks[sortedIdx], from = m ? m.t : 0, recorded = false;
                return {
                    to: function (t) {
                        if (!m || !fits(t, m.id)) return false;
                        if (!recorded) { record(); recorded = true; }
                        m.t = t;
                        sortMarks();
                        changed('drag');
                        return true;
                    },
                    end: function () {
                        if (!m) return;
                        if (recorded && m.t === from) { undo.pop(); }
                        if (recorded) changed('marks');
                    },
                    time: function () { return m ? m.t : 0; }
                };
            },
            /* a whole new set (silences, equal parts, pasted timestamps, CUE, the file's own chapters):
               list = [{t, name?}]; firstName names chapter 0 */
            replaceMarks: function (list, firstName) {
                record();
                marks = []; attrs = {}; ins = {}; order = null;
                var sorted = (list || []).slice().sort(function (a, b) { return a.t - b.t; });
                for (var i = 0; i < sorted.length; i++) {
                    var t = sorted[i].t;
                    if (!(t >= minGap && t <= duration - minGap)) continue;
                    if (marks.length && t - marks[marks.length - 1].t < minGap) continue;
                    var id = nextId++;
                    marks.push({ id: id, t: t });
                    if (sorted[i].name) attrs[id] = { name: String(sorted[i].name), fadeIn: 0, fadeOut: 0 };
                }
                if (firstName) attrs[0] = { name: String(firstName), fadeIn: 0, fadeOut: 0 };
                changed('marks');
                return marks.length;
            },
            checkpoint: function () { armed = true; },
            setName: function (i, s, commit) {
                var key = keyOf(i);
                if (key < 0) return;
                var a = attrs[key] || (attrs[key] = { name: '', fadeIn: 0, fadeOut: 0 });
                if (a.name === String(s || '')) { if (commit) armed = true; return; }
                if (armed) { record(); armed = false; }
                a.name = String(s || '');
                if (commit) armed = true;
                changed('name');
            },
            setFade: function (i, which, v, commit) {
                var key = keyOf(i);
                if (key < 0 || (which !== 'fadeIn' && which !== 'fadeOut')) return;
                var a = attrs[key] || (attrs[key] = { name: '', fadeIn: 0, fadeOut: 0 });
                v = num(v, 0, 600, 0);
                if (a[which] === v) { if (commit) armed = true; return; }
                if (armed) { record(); armed = false; }
                a[which] = v;
                if (commit) armed = true;
                changed('fade');
            },
            /* the insert before chapter i (i ≥ 1), or null to remove it */
            setInsert: function (i, insert) {
                var key = keyOf(i);
                if (i < 1 || key < 0) return false;
                record();
                if (insert) ins[key] = insert; else delete ins[key];
                changed('insert');
                return true;
            },
            /* move the file at position `from` of the ordered list to position `to` */
            move: function (from, to) {
                var ch = api.ordered();
                if (from === to || from < 0 || to < 0 || from >= ch.length || to >= ch.length) return false;
                record();
                var keys = ch.map(function (c) { return c.key; });
                var k = keys.splice(from, 1)[0];
                keys.splice(to, 0, k);
                var natural = keys.every(function (x, j) { return x === keyOf(j); });
                order = natural ? null : keys;
                changed('order');
                return true;
            },
            canUndo: function () { return undo.length > 0; },
            canRedo: function () { return redo.length > 0; },
            undo: function () {
                if (!undo.length) return false;
                redo.push(snap());
                restore(undo.pop());
                armed = true;
                changed('undo');
                return true;
            },
            redo: function () {
                if (!redo.length) return false;
                undo.push(snap());
                restore(redo.pop());
                armed = true;
                changed('redo');
                return true;
            },
            empty: function () { return !marks.length && !Object.keys(attrs).length && !Object.keys(ins).length; },
            /* plain data for the work store; the File of an audio insert goes into `files` */
            toJSON: function (files) {
                var o2 = { v: 1, duration: duration, marks: marks.map(function (m) { return [m.id, Math.round(m.t * 1e6) / 1e6]; }),
                    attrs: {}, ins: {}, order: order ? order.slice() : null, nextId: nextId }, k;
                for (k in attrs) if (Object.prototype.hasOwnProperty.call(attrs, k)) {
                    var a = attrs[k];
                    if (a.name || a.fadeIn || a.fadeOut) o2.attrs[k] = { n: a.name || '', i: a.fadeIn || 0, o: a.fadeOut || 0 };
                }
                for (k in ins) if (Object.prototype.hasOwnProperty.call(ins, k)) {
                    var s = ins[k];
                    if (s.type === 'silence') o2.ins[k] = { type: 'silence', d: s.duration };
                    else if (s.type === 'audio') {
                        var f = -1;
                        if (files && s.file) { f = files.length; files.push(s.file); }
                        o2.ins[k] = { type: 'audio', d: s.duration, n: s.name || '', f: f };
                    }
                }
                return o2;
            },
            _load: function (j, files, missing) {
                marks = []; attrs = {}; ins = {}; order = null; undo = []; redo = []; armed = true;
                var maxId = 0, seen = {};
                (Array.isArray(j.marks) ? j.marks : []).forEach(function (m) {
                    var id = Array.isArray(m) ? m[0] : m.id, t = Array.isArray(m) ? m[1] : m.t;
                    if (!(id > 0) || seen[id] || !(t >= minGap && t <= duration - minGap)) return;
                    seen[id] = 1;
                    marks.push({ id: id, t: t });
                    if (id > maxId) maxId = id;
                });
                sortMarks();
                for (var i = 1; i < marks.length; i++) if (marks[i].t - marks[i - 1].t < minGap) { marks.splice(i, 1); i--; }
                var live = { 0: 1 };
                marks.forEach(function (m) { live[m.id] = 1; });
                var A = j.attrs || {}, k;
                for (k in A) if (live[k] && A[k]) attrs[k] = { name: String(A[k].n || ''), fadeIn: num(A[k].i, 0, 600, 0), fadeOut: num(A[k].o, 0, 600, 0) };
                var S = j.ins || {};
                for (k in S) {
                    if (!live[k] || +k === 0 || !S[k]) continue;
                    if (S[k].type === 'silence') ins[k] = { type: 'silence', duration: num(S[k].d, 0.1, 60, 2) };
                    else if (S[k].type === 'audio') {
                        var f = (typeof S[k].f === 'number' && S[k].f >= 0 && files) ? files[S[k].f] : null;
                        if (f) ins[k] = { type: 'audio', file: f, name: S[k].n || f.name || '', duration: num(S[k].d, 0, 1e6, 0) };
                        else if (missing) missing.push(S[k].n || '?');
                    }
                }
                if (Array.isArray(j.order) && j.order.length === marks.length + 1 && j.order.every(function (x) { return live[x]; })) order = j.order.slice();
                nextId = Math.max(maxId + 1, j.nextId > 0 ? j.nextId : 1);
                changed('load');
            }
        };
        return api;
    }

    function fromJSON(j, files, opts) {
        opts = opts || {};
        var p = create({ duration: opts.duration > 0 ? opts.duration : (j && j.duration) || 0, minGap: opts.minGap });
        p._load(j || {}, files || [], opts.missing || null);
        return p;
    }

    root.CsProject = { create: create, fromJSON: fromJSON };
})(typeof window !== 'undefined' ? window : globalThis);
